import { Link } from "react-router-dom";
import { ArrowUpRight, Search, PenTool, Code2, Rocket } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import ServiceCard from "@/components/ServiceCard";
import WorldMap from "@/components/WorldMap";
import { SERVICES, IMAGES } from "@/lib/site";

const PROCESS = [
  { icon: Search, num: "01", title: "Discover", desc: "We map your goals, constraints and users, then pressure-test the plan before a line of code." },
  { icon: PenTool, num: "02", title: "Design", desc: "Architecture, security model and interface designed together — glossy on the surface, robust underneath." },
  { icon: Code2, num: "03", title: "Build", desc: "Iterative delivery with CI/CD, automated tests and observability wired in from day one." },
  { icon: Rocket, num: "04", title: "Scale", desc: "Launch, monitor and optimise across clouds with our follow-the-sun SRE team." },
];

const STACK = ["AWS", "Azure", "Alibaba Cloud", "Kubernetes", "Terraform", "PostgreSQL", "React", "Python", "Go", "Kafka", "Redis", "GraphQL"];

function InnerHero() {
  return (
    <section className="relative overflow-hidden pb-10 pt-40" data-testid="services-hero">
      <div className="absolute inset-0">
        <img src={IMAGES.servers} alt="" className="h-full w-full img-tech object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#e9eef5]/80 via-[#e9eef5]/85 to-[#e9eef5]" />
      </div>
      <div className="relative z-10 mx-auto max-w-6xl px-6">
        <Reveal>
          <Overline>Services</Overline>
          <h1 className="mt-5 max-w-4xl font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
            <span className="text-slate-900">Expertise that spans </span>
            <span className="metallic-text">the full stack of ambition.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            Six deeply-specialised practices, engineered to work as one. From
            first prototype to planet-scale, we cover the whole journey.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Bento() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16" data-testid="services-grid">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={(i % 3) * 0.08} className={s.span}>
            <ServiceCard service={s} />
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-24" data-testid="process">
      <Reveal>
        <Overline>How we work</Overline>
        <h2 className="mt-4 max-w-2xl font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
          A calm, repeatable path from idea to impact.
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {PROCESS.map((p, i) => (
          <Reveal key={p.num} delay={i * 0.08}>
            <div className="glass hover-lift h-full rounded-3xl p-7">
              <div className="flex items-center justify-between">
                <p.icon size={22} className="text-blue-600" />
                <span className="font-mono-x text-xl text-slate-900/15">{p.num}</span>
              </div>
              <h3 className="mt-5 font-display text-xl font-bold text-slate-900">{p.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Stack() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16" data-testid="tech-stack">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <Overline>Global delivery</Overline>
          <h2 className="mt-4 font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            One team, every timezone, any cloud.
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600 md:text-lg">
            Our engineers are cloud-agnostic and battle-tested. Whatever your
            stack, we speak it fluently.
          </p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {STACK.map((t) => (
              <span
                key={t}
                className="rounded-full border border-slate-900/10 bg-slate-900/[0.04] px-4 py-2 text-sm text-slate-700 transition-colors hover:border-blue-400/40 hover:text-slate-900"
              >
                {t}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="aspect-[5/3] w-full">
            <WorldMap />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16" data-testid="services-cta">
      <Reveal className="relative overflow-hidden rounded-[2rem] border border-blue-500/20 bg-gradient-to-br from-blue-100 via-[#f3f6fa] to-[#e9eef5] p-10 text-center md:p-16">
        <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-[60%] -translate-x-1/2 rounded-full bg-blue-600/20 blur-[100px]" />
        <div className="relative z-10">
          <h2 className="mx-auto max-w-2xl font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            Let&apos;s scope your next build.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-slate-600 md:text-lg">
            A short conversation is the fastest way to know if we&apos;re the right team.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta-button"
            className="glossy-btn mt-9 inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
          >
            Book a consultation <ArrowUpRight size={18} />
          </Link>
        </div>
      </Reveal>
    </section>
  );
}

function FeatureBand() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16" data-testid="services-feature-band">
      <div className="grid gap-5 md:grid-cols-3">
        <Reveal className="md:col-span-2">
          <div className="group relative h-72 overflow-hidden rounded-3xl border border-slate-900/10 md:h-full">
            <img src={IMAGES.render3d} alt="Abstract 3D engineering" loading="lazy" className="h-full w-full img-tech object-cover opacity-75 transition-transform duration-[1.2s] group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#e9eef5] via-[#e9eef5]/30 to-transparent" />
            <div className="absolute bottom-7 left-7 right-7">
              <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-blue-600">Engineering-first</p>
              <h3 className="mt-2 font-display text-2xl font-bold text-slate-900 md:text-3xl">
                Systems designed to outlast the hype cycle.
              </h3>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="group relative h-72 overflow-hidden rounded-3xl border border-slate-900/10 md:h-full">
            <img src={IMAGES.circuit} alt="Circuit board" loading="lazy" className="h-full w-full img-tech object-cover opacity-70 transition-transform duration-[1.2s] group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#e9eef5] via-[#e9eef5]/40 to-transparent" />
            <div className="absolute bottom-7 left-7 right-7">
              <div className="metallic-text font-display text-4xl font-extrabold">TP99.99</div>
              <p className="mt-1 text-sm text-slate-700">Reliability target across every deployment.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <div data-testid="services-page">
      <InnerHero />
      <Bento />
      <FeatureBand />
      <Process />
      <Stack />
      <CtaBand />
    </div>
  );
}
